export const CREATE_JOB_DRAFT_SCHEMA_VERSION = 1;
export const CREATE_JOB_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export type CreateJobDraftAction = 'draft' | 'publish' | null;

export type CreateJobDraftFormValues = {
  title?: string;
  department?: string;
  location?: string;
  job_type?: string;
  employment_type?: string;
  seniority?: string;
  min_experience_years?: string | number;
  education_level?: string;
  notice_period?: string | number;
  start_date?: string;
  salary_min?: string | number;
  salary_max?: string | number;
  salary_currency?: string;
  description?: string;
  requirements?: string;
  cv_pass_threshold?: string | number;
  interview_pass_threshold?: string | number;
  open_duration_days?: string | number;
  visibility_mode?: string;
  number_of_openings?: string | number;
};

export type CreateJobDraftState = {
  step: number;
  formValues: CreateJobDraftFormValues;
  databaseDraftJobId?: string | null;
  publishIdempotencyKey?: string | null;
  skills: string[];
  skillInput: string;
  certifications: string[];
  certInput: string;
  languages: string[];
  langInput: string;
  benefits: string[];
  benefitInput: string;
  responsibilities: string;
  pendingAction: CreateJobDraftAction;
  duplicateWarning: boolean;
};

export type StoredCreateJobDraft = {
  version: typeof CREATE_JOB_DRAFT_SCHEMA_VERSION;
  savedAt: number;
  expiresAt: number;
  draft: CreateJobDraftState;
};

const FORM_KEYS: Array<keyof CreateJobDraftFormValues> = [
  'title',
  'department',
  'location',
  'job_type',
  'employment_type',
  'seniority',
  'min_experience_years',
  'education_level',
  'notice_period',
  'start_date',
  'salary_min',
  'salary_max',
  'salary_currency',
  'description',
  'requirements',
  'cv_pass_threshold',
  'interview_pass_threshold',
  'open_duration_days',
  'visibility_mode',
  'number_of_openings',
];

const stringValue = (value: unknown) => (typeof value === 'string' ? value : '');
const optionalStringValue = (value: unknown) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
const stringArrayValue = (value: unknown) =>
  Array.isArray(value)
    ? Array.from(new Set(
        value
          .filter((item): item is string => typeof item === 'string')
          .map((item) => item.trim())
          .filter(Boolean),
      )).slice(0, 100)
    : [];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const LEGACY_ENUM_VALUES: Partial<Record<keyof CreateJobDraftFormValues, Record<string, string>>> = {
  job_type: {
    onsite: 'onsite',
    'on-site': 'onsite',
    remote: 'remote',
    hybrid: 'hybrid',
  },
  employment_type: {
    'full-time': 'full-time',
    full_time: 'full-time',
    'part-time': 'part-time',
    part_time: 'part-time',
    contract: 'contract',
    freelancer: 'freelancer',
    internship: 'internship',
  },
  seniority: {
    tamheer: 'tamheer',
    intern: 'intern',
    entry: 'entry',
    'entry-level': 'entry',
    mid: 'mid',
    'mid-level': 'mid',
    senior: 'senior',
    lead: 'lead',
    manager: 'manager',
    director: 'director',
    vp: 'vp',
    'c-level': 'c-level',
    c_level: 'c-level',
  },
  education_level: {
    none: 'none',
    'high-school': 'high-school',
    high_school: 'high-school',
    diploma: 'diploma',
    bachelors: 'bachelors',
    masters: 'masters',
    doctorate: 'doctorate',
  },
  visibility_mode: {
    direct_link_only: 'direct_link_only',
    link_only: 'direct_link_only',
    seek_public: 'seek_public',
    public: 'seek_public',
  },
};

const NUMERIC_FORM_KEYS = new Set<keyof CreateJobDraftFormValues>([
  'min_experience_years',
  'notice_period',
  'salary_min',
  'salary_max',
  'cv_pass_threshold',
  'interview_pass_threshold',
  'open_duration_days',
  'number_of_openings',
]);

function sanitizeFormValue(key: keyof CreateJobDraftFormValues, value: unknown): string | number | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  if (typeof value === 'number' && !Number.isFinite(value)) return undefined;

  if (key === 'start_date') {
    const date = String(value).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
    const parsed = new Date(`${date}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date ? '' : date;
  }

  const enumValues = LEGACY_ENUM_VALUES[key];
  if (enumValues) {
    return enumValues[String(value).trim().toLowerCase()] || '';
  }

  if (NUMERIC_FORM_KEYS.has(key)) {
    return typeof value === 'string' ? value.trim() : value;
  }

  return value;
}

function sanitizeFormValues(value: unknown): CreateJobDraftFormValues {
  if (!value || typeof value !== 'object') return {};
  const source = value as Record<string, unknown>;
  return FORM_KEYS.reduce<CreateJobDraftFormValues>((acc, key) => {
    const next = sanitizeFormValue(key, source[key]);
    if (next !== undefined) {
      acc[key] = next as never;
    }
    return acc;
  }, {});
}

export function buildCreateJobDraftStorageKey(args: { orgId?: string | null; userId?: string | null }) {
  return [
    'forkhire',
    'create-job-draft',
    `v${CREATE_JOB_DRAFT_SCHEMA_VERSION}`,
    args.orgId || 'no-org',
    args.userId || 'no-user',
  ].join(':');
}

export function sanitizeCreateJobDraftState(value: unknown): CreateJobDraftState | null {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  return {
    step: Math.min(Math.max(Number(source.step) || 0, 0), 3),
    formValues: sanitizeFormValues(source.formValues),
    databaseDraftJobId: (() => {
      const id = optionalStringValue(source.databaseDraftJobId);
      return id && UUID_PATTERN.test(id) ? id : null;
    })(),
    publishIdempotencyKey: (() => {
      const id = optionalStringValue(source.publishIdempotencyKey);
      return id && UUID_PATTERN.test(id) ? id : null;
    })(),
    skills: stringArrayValue(source.skills),
    skillInput: stringValue(source.skillInput),
    certifications: stringArrayValue(source.certifications),
    certInput: stringValue(source.certInput),
    languages: stringArrayValue(source.languages),
    langInput: stringValue(source.langInput),
    benefits: stringArrayValue(source.benefits),
    benefitInput: stringValue(source.benefitInput),
    responsibilities: stringValue(source.responsibilities),
    pendingAction: source.pendingAction === 'draft' || source.pendingAction === 'publish' ? source.pendingAction : null,
    duplicateWarning: source.duplicateWarning === true,
  };
}

export function hasMeaningfulCreateJobDraftContent(draft: CreateJobDraftState) {
  const formHasContent = Object.entries(draft.formValues).some(([key, value]) => {
    if (key === 'job_type') return value !== 'onsite';
    if (key === 'employment_type') return value !== 'full-time';
    if (key === 'salary_currency') return value !== 'SAR';
    if (key === 'cv_pass_threshold') return Number(value) !== 80;
    if (key === 'interview_pass_threshold') return Number(value) !== 70;
    if (key === 'open_duration_days') return Number(value) !== 30;
    if (key === 'visibility_mode') return value !== 'direct_link_only';
    if (key === 'number_of_openings') return Number(value) !== 1;
    return String(value ?? '').trim().length > 0;
  });

  return (
    formHasContent ||
    Boolean(draft.databaseDraftJobId) ||
    Boolean(draft.publishIdempotencyKey) ||
    draft.skills.length > 0 ||
    draft.certifications.length > 0 ||
    draft.languages.length > 0 ||
    draft.benefits.length > 0 ||
    draft.skillInput.trim().length > 0 ||
    draft.certInput.trim().length > 0 ||
    draft.langInput.trim().length > 0 ||
    draft.benefitInput.trim().length > 0 ||
    draft.responsibilities.trim().length > 0
  );
}

export function createStoredCreateJobDraft(draft: CreateJobDraftState, now = Date.now()): StoredCreateJobDraft {
  return {
    version: CREATE_JOB_DRAFT_SCHEMA_VERSION,
    savedAt: now,
    expiresAt: now + CREATE_JOB_DRAFT_TTL_MS,
    draft: sanitizeCreateJobDraftState(draft) ?? {
      step: 0,
      formValues: {},
      databaseDraftJobId: null,
      publishIdempotencyKey: null,
      skills: [],
      skillInput: '',
      certifications: [],
      certInput: '',
      languages: [],
      langInput: '',
      benefits: [],
      benefitInput: '',
      responsibilities: '',
      pendingAction: null,
      duplicateWarning: false,
    },
  };
}

export function parseStoredCreateJobDraft(raw: string | null, now = Date.now()): StoredCreateJobDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredCreateJobDraft>;
    if (parsed.version !== CREATE_JOB_DRAFT_SCHEMA_VERSION) return null;
    if (typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= now) return null;
    const draft = sanitizeCreateJobDraftState(parsed.draft);
    if (!draft || !hasMeaningfulCreateJobDraftContent(draft)) return null;
    return {
      version: CREATE_JOB_DRAFT_SCHEMA_VERSION,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : now,
      expiresAt: parsed.expiresAt,
      draft,
    };
  } catch {
    return null;
  }
}

export function readCreateJobDraft(storage: Pick<Storage, 'getItem'>, key: string, now = Date.now()) {
  return parseStoredCreateJobDraft(storage.getItem(key), now)?.draft ?? null;
}

export function writeCreateJobDraft(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  key: string,
  draft: CreateJobDraftState,
  now = Date.now(),
) {
  if (!hasMeaningfulCreateJobDraftContent(draft)) {
    storage.removeItem(key);
    return;
  }
  storage.setItem(key, JSON.stringify(createStoredCreateJobDraft(draft, now)));
}

export function clearCreateJobDraft(storage: Pick<Storage, 'removeItem'>, key: string) {
  storage.removeItem(key);
}
